import * as angular from "angular";
import * as _ from "lodash";
import {IController} from "angular";
import {
  ConsiderVehicleLocation,
  ConstraintConfigurationModel,
  ConstraintConfigurationsModel,
  VisitScheduleOptimizationSettings
} from "../../models/visit-schedule-optimization-config.model";
import {
  SchedulingProjectProviderType,
  SchedulingProjectVisitCallProviderType,
  VisitPlanAccountImportMode,
  VisitPlanAgentImportMode
} from "../../models/visit-plan-imports.model";
import {
  AutoDispatchTimeRange,
  VisitScheduleDispatchSettingsModel
} from "../../models/visit-schedule-dispatch-settings.model";
import {
  buildAutoDispatchTimeRanges,
  findAutoDispatchTimeRange
} from "../../models/visit-schedule-dispatch-settings.functions";
import {VisitScheduleModel} from "../../models/visit-schedule.model";
import {
  SCHEDULING_PROJECT_TEMPLATE_SOFT_CONSTRAINTS_MAP
} from "../../angular/constants/scheduling-project.constant";
import {SplitService} from "../../../commons/services/split-service.service";
import {PlanningServerEnum} from "../../enums/planning-server.enum";
import {VisitScheduleResourceSettingsModel} from "../../models/visit-schedule-resource-settings.model";
import {SchedulingProjectTemplateAccessControlsModel} from "../../models/scheduling-project-template.model";
import {
  TemplateProviderOptionsModel,
  TemplateProviderSelectionModel
} from "../../models/scheduling-project-template-providers.model";
import {hasArchivedProviderSelected} from "../../models/scheduling-project-template-providers.functions";
import {getMetersPerLargeDistanceUnit} from "../../../commons/utils/distance-unit.util";

export class OptimizeVisitScheduleDialogFormComponent implements IController {

  form?: any;
  isTemplate?: boolean;
  visitScheduleOptimizationSettings: VisitScheduleOptimizationSettings;
  selectedVisitDays = [];
  visitDays = [];
  tabs: string[] = [];
  scheduleProject: VisitScheduleModel;
  providerOptions: TemplateProviderOptionsModel;
  templateProviderSelection: TemplateProviderSelectionModel;
  visitScheduleDispatchSettings: VisitScheduleDispatchSettingsModel;
  visitScheduleResourceSettings: VisitScheduleResourceSettingsModel;
  visitPlanSettings: {autoTriggerRecalcAfterPullingActualData?: boolean};
  templateAccessControls: SchedulingProjectTemplateAccessControlsModel;
  onSelectServiceTimeProvider: (params: { value: any }) => void;
  onSelectUnitsProvider: (params: { value: any }) => void;
  onSelectDeliveryTimeProvider: (params: { value: any }) => void;
  onSelectServiceTimeVisitCallPredictionProvider: (params: { value: any }) => void;

  public selectedTab: string;
  public invalidTabs: string[] = [];
  public dispatchedDays : Set<any>;
  public ConsiderVehicleLocation = ConsiderVehicleLocation;
  public considerVehicleLocationOptions = Object.values(ConsiderVehicleLocation)
    .map(value => ({
      value,
      label: value
    }));
  public readonly deliveryProviderTypes: string[] = Object.values(SchedulingProjectProviderType);
  public readonly planningServerTypes: string[] = Object.values(PlanningServerEnum);
  public readonly visitCallProviderTypes: string[] = Object.values(SchedulingProjectVisitCallProviderType);
  public readonly accountImportModes: string[] = Object.values(VisitPlanAccountImportMode);
  public readonly agentImportModes: string[] = Object.values(VisitPlanAgentImportMode);

  public autoDispatchEnabled: boolean = false;
  public autoDispatchTimeRanges: AutoDispatchTimeRange[] = buildAutoDispatchTimeRanges();
  public autoDispatchSelectedRange: AutoDispatchTimeRange;
  public readonly days = this.commonsConstants.daysCheckboxConfig;
  public selectedStartDays: boolean[] = Array(7).fill(false);
  public selectedEndDays: boolean[] = Array(7).fill(false);
  public readonly CONSTRAINTS_DATA = SCHEDULING_PROJECT_TEMPLATE_SOFT_CONSTRAINTS_MAP;
  public readonly enableConstraintConfiguration: boolean = this.splitService.getSiteInfo().enableConstraintConfiguration ?? false;
  public siteDistanceUnit: string;
  public templateGroupsBranchIds: string[];

  constructor(private commonsConstants, private splitService: SplitService, private $groups) {
    this.siteDistanceUnit = this.splitService.getSiteDistanceLargeUnit(true);
  }

  $onInit() {
    this.initDate();
    this.initAutoDispatch();
    this._initStopToStopSettings();

    if (this.isTemplate) {
      this.templateAccessControls = this.templateAccessControls || {
        global: true,
        branchIds: [],
        groupIds: []
      };
      this._initTemplateGroupsBranchIds();
      this._updateTemplateAccessFormValidity();
    }

    if (!this.scheduleProject) {
      return;
    }

    if (!this.scheduleProject.deliveryProviderType) {
      this.scheduleProject.deliveryProviderType = SchedulingProjectProviderType.System;
    }

    if (!this.scheduleProject.visitCallProviderType) {
      this.scheduleProject.visitCallProviderType = SchedulingProjectVisitCallProviderType.VisitCall;
    }

    if (!this.scheduleProject.accountImportMode) {
      this.scheduleProject.accountImportMode = VisitPlanAccountImportMode.System;
    }

    if (!this.scheduleProject.agentImportMode) {
      this.scheduleProject.agentImportMode = VisitPlanAgentImportMode.System;
    }

    this._updateSelectedAllowedDays();
  }

  /**
   * A provider kind with no data source configured cannot be picked, and its select is left out of
   * the form - so the template would quietly be saved without that provider unless we say so.
   */
  get hasUnconfiguredProviderKinds(): boolean {
    return !!this.providerOptions &&
      [this.providerOptions.serviceTime, this.providerOptions.units, this.providerOptions.deliveryTime]
        .some((wrapper) => !wrapper.list.length);
  }

  get hasArchivedProviderSelected(): boolean {
    return hasArchivedProviderSelected(this.providerOptions, this.templateProviderSelection);
  }

  get stopToStopDistanceLimit(): number | undefined {
    return this._metersToLargeDistanceUnit(this._stopToStopDistanceConstraint.distanceLimit);
  }

  set stopToStopDistanceLimit(value: number | undefined) {
    this._stopToStopDistanceConstraint.distanceLimit = this._largeDistanceUnitToMeters(value);
  }

  get stopToStopHighTierEnabled(): boolean {
    const constraint = this._stopToStopDistanceConstraint;
    return !_.isNil(constraint.highDistanceLimit) ||
      !_.isNil(constraint.highWeight) ||
      this._isStopToStopFieldCleared('stopToStopHighDistanceLimit');
  }

  set stopToStopHighTierEnabled(enabled: boolean) {
    const constraint = this._stopToStopDistanceConstraint;
    if (enabled) {
      constraint.highDistanceLimit = !_.isNil(constraint.distanceLimit) ? constraint.distanceLimit : 0;
      constraint.highWeight = !_.isNil(constraint.weight) ? constraint.weight : 0;
    } else {
      delete constraint.highDistanceLimit;
      delete constraint.highWeight;
      this._clearStopToStopFieldValue('stopToStopHighDistanceLimit');
    }
  }

  get stopToStopHighDistanceLimit(): number | undefined {
    return this._metersToLargeDistanceUnit(this._stopToStopDistanceConstraint.highDistanceLimit);
  }

  set stopToStopHighDistanceLimit(value: number | undefined) {
    this._stopToStopDistanceConstraint.highDistanceLimit = this._largeDistanceUnitToMeters(value);
  }

  get stopToStopHardLimitEnabled(): boolean {
    return !_.isNil(this._stopToStopDistanceConstraint.hardDistanceLimit) ||
      this._isStopToStopFieldCleared('stopToStopHardDistanceLimit');
  }

  set stopToStopHardLimitEnabled(enabled: boolean) {
    const constraint = this._stopToStopDistanceConstraint;
    if (enabled) {
      const fallback = !_.isNil(constraint.highDistanceLimit) ? constraint.highDistanceLimit : constraint.distanceLimit;
      constraint.hardDistanceLimit = !_.isNil(fallback) ? fallback : 0;
    } else {
      delete constraint.hardDistanceLimit;
      this._clearStopToStopFieldValue('stopToStopHardDistanceLimit');
    }
  }

  get stopToStopHardDistanceLimit(): number | undefined {
    return this._metersToLargeDistanceUnit(this._stopToStopDistanceConstraint.hardDistanceLimit);
  }

  set stopToStopHardDistanceLimit(value: number | undefined) {
    this._stopToStopDistanceConstraint.hardDistanceLimit = this._largeDistanceUnitToMeters(value);
  }

  get stopToStopHighMinLimit(): number {
    return _.defaultTo(this.stopToStopDistanceLimit, 0);
  }

  get stopToStopHardMinLimit(): number {
    return _.defaultTo(
      this.stopToStopHighDistanceLimit,
      _.defaultTo(this.stopToStopDistanceLimit, 0)
    );
  }

  onStopToStopDistanceLimitChange() {
    this._revalidateStopToStopDependents(['stopToStopHighDistanceLimit', 'stopToStopHardDistanceLimit']);
  }

  onStopToStopHighDistanceLimitChange() {
    this._revalidateStopToStopDependents(['stopToStopHardDistanceLimit']);
  }

  onStopToStopHardDistanceLimitChange() {
  }

  private initDate() {
    this.selectedTab = this.tabs[0];
    this.dispatchedDays = new Set(this.visitDays.filter(day => this.isDayDispatched(day)))
  }

  private isDayDispatched(day) {
    let totals = day.totals;
    return totals?.dispatchedRoutes > 0
      && totals?.dispatchedRoutes == totals?.totalRoutes
      && (totals?.totalLeftovers == 0 || totals?.dispatchedLeftovers > 0
      && totals?.dispatchedLeftovers == totals?.totalLeftovers)
  }

  onSelectTab(tab) {
    this.selectedTab = tab;
  }

  onToggleGlobalTemplate() {
    if (!this.templateAccessControls) {
      this.templateAccessControls = {global: true, branchIds: [], groupIds: []};
    }

    this.templateAccessControls.global = !this.templateAccessControls.global;

    if (this.templateAccessControls.global) {
      this.templateAccessControls.branchIds = [];
      this.templateAccessControls.groupIds = [];
      this.templateAccessControls.groupsBranchIds = undefined;
      this.templateGroupsBranchIds = undefined;
    }

    this._updateTemplateAccessFormValidity();
  }

  onTemplateGroupsSelect(groups, selectedIds) {
    if (!this.templateAccessControls) {
      this.templateAccessControls = {global: false, branchIds: [], groupIds: []};
    }

    this.templateAccessControls.groupIds = selectedIds || [];
    this.templateGroupsBranchIds = groups?.length ? _.uniq(_.flatMap(groups, 'branchIds')) : undefined;
    this.templateAccessControls.groupsBranchIds = this.templateGroupsBranchIds;
    this._updateTemplateAccessFormValidity();
  }

  onTemplateBranchesSelect(branches, selectedIds) {
    if (!this.templateAccessControls) {
      this.templateAccessControls = {global: false, branchIds: [], groupIds: []};
    }

    this.templateAccessControls.branchIds = selectedIds || [];
    this._updateTemplateAccessFormValidity();
  }

  hasErrorsInTab(tab) {
    return this.invalidTabs.includes(tab);
  }

  toggleSwitch(field: string) {
    this.visitScheduleOptimizationSettings[field] = !this.visitScheduleOptimizationSettings[field];
  }

  onToggleConfiguration(field: string) {
    const enabled: boolean = this.visitScheduleOptimizationSettings.constraintConfigurations[field].enableConfiguration;
    this.visitScheduleOptimizationSettings.constraintConfigurations[field].enableConfiguration = !enabled;
  }

  isDisableSelection(day) {
    return this.dispatchedDays.has(day);
  }

  isSelected(day: any): boolean {
    if (!this.selectedVisitDays || !day) return false;
    return this.selectedVisitDays.some(selectedDay =>
      selectedDay && day && selectedDay.id === day.id
    );
  }

  onToggleSelect(day: any): void {
    if (!day) return;

    this.selectedVisitDays = this.selectedVisitDays || [];

    if (this.isSelected(day)) {
      this.selectedVisitDays = this.selectedVisitDays.filter(
        selectedDay => selectedDay && day && selectedDay.id !== day.id
      );
    } else {
      this.selectedVisitDays = [...this.selectedVisitDays, day];
    }
  }

  onToggleSelectAll(): void {
    if (!this.visitDays) return;

    if (this.selectedVisitDays && this.selectedVisitDays.length === this.visitDays.length - this.dispatchedDays.size) {
      this.selectedVisitDays = [];
    } else {
      this.selectedVisitDays = [...this.visitDays.filter(day => !this.dispatchedDays.has(day))];
    }
  }

  onSelectPlanningServer(planningServer: PlanningServerEnum) {
    this.visitScheduleOptimizationSettings.planningServer = planningServer;
  }

  onSelectDeliveryProviderType(value) {
    this.scheduleProject.deliveryProviderType = value;
  }

  onSelectVisitCallProviderType(value) {
    this.scheduleProject.visitCallProviderType = value;
  }

  onSelectAccountImportMode(value) {
    this.scheduleProject.accountImportMode = value;
  }

  onSelectAgentImportMode(value) {
    this.scheduleProject.agentImportMode = value;
  }

  onAutoDispatchTimeRangeSelect(): void {
    this.syncDispatchSettingsFromUi();
  }

  toggleStartDay(day: number) {
    this.selectedStartDays[day] = !this.selectedStartDays[day];
    this.scheduleProject.schedulingProjectRules.allowedStartDays = this.selectedStartDays.reduce((acc, value, index) => {
      if (value) {
        acc.push(this.commonsConstants.dayArray[index]);
      }

      return acc;
    }, []);
    this._updateFormValidity();
  }

  toggleEndDay(day: number) {
    this.selectedEndDays[day] = !this.selectedEndDays[day];
    this.scheduleProject.schedulingProjectRules.allowedEndDays = this.selectedEndDays.reduce((acc, value, index) => {
      if (value) {
        acc.push(this.commonsConstants.dayArray[index]);
      }

      return acc;
    }, []);
    this._updateFormValidity();
  }

  private initAutoDispatch(): void {
    const s = this.visitScheduleDispatchSettings;
    this.autoDispatchEnabled = s?.autoDispatchEnabled ?? false;
    this.autoDispatchSelectedRange = findAutoDispatchTimeRange(
      this.autoDispatchTimeRanges,
      s?.autoDispatchTimeFrom,
      s?.autoDispatchTimeTo
    );
  }

  public syncDispatchSettingsFromUi(): void {
    this.visitScheduleDispatchSettings = {
      autoDispatchEnabled: this.autoDispatchEnabled,
      autoDispatchTimeFrom: this.autoDispatchSelectedRange?.from,
      autoDispatchTimeTo: this.autoDispatchSelectedRange?.to,
    };
  }

  public selectAllDays(target: string) {
    if (this.scheduleProject.schedulingProjectRules[target].length === 7) {
      this.scheduleProject.schedulingProjectRules[target] = [];
    } else {
      this.scheduleProject.schedulingProjectRules[target] = this.commonsConstants.dayArray;
    }

    this._updateSelectedAllowedDays();
  }

  private _updateSelectedAllowedDays() {
    for (let index = 0; index < this.selectedStartDays.length; index++) {
      this.selectedStartDays[index] = this.scheduleProject.schedulingProjectRules.allowedStartDays.includes(this.commonsConstants.dayArray[index]);
    }

    for (let index = 0; index < this.selectedEndDays.length; index++) {
      this.selectedEndDays[index] = this.scheduleProject.schedulingProjectRules.allowedEndDays.includes(this.commonsConstants.dayArray[index]);
    }

    this._updateFormValidity();
  }

  private _updateFormValidity() {
    const startValid: boolean = this.scheduleProject.schedulingProjectRules.allowedStartDays.length > 0;
    const endValid: boolean = this.scheduleProject.schedulingProjectRules.allowedEndDays.length > 0;
    this.form?.$setValidity('isValid', startValid && endValid);
    this._updateTemplateAccessFormValidity();
  }

  private _updateTemplateAccessFormValidity() {
    if (!this.isTemplate || !this.templateAccessControls || this.templateAccessControls.global) {
      this.form?.$setValidity('templateAccessControls', true);
      return;
    }

    const hasScope = !!this.templateAccessControls.branchIds?.length || !!this.templateAccessControls.groupIds?.length;
    this.form?.$setValidity('templateAccessControls', hasScope);
  }

  private _initTemplateGroupsBranchIds() {
    if (!this.templateAccessControls?.groupIds?.length) {
      return;
    }

    this.$groups().$asyncValues(this.templateAccessControls.groupIds)
      .then((groups) => {
        this.templateGroupsBranchIds = _.uniq(_.flatMap(groups, 'branchIds'));
        this.templateAccessControls.groupsBranchIds = this.templateGroupsBranchIds;
      });
  }

  private _initStopToStopSettings() {
    if (!this.visitScheduleOptimizationSettings) {
      return;
    }
    if (!this.visitScheduleOptimizationSettings.constraintConfigurations) {
      this.visitScheduleOptimizationSettings.constraintConfigurations = {} as ConstraintConfigurationsModel;
    }
    if (!this.visitScheduleOptimizationSettings.constraintConfigurations.stopToStopDistanceConstraint) {
      this.visitScheduleOptimizationSettings.constraintConfigurations.stopToStopDistanceConstraint = {
        enableConfiguration: false,
        weight: 0
      } as ConstraintConfigurationModel;
    }
  }

  private get _stopToStopDistanceConstraint() {
    return this.visitScheduleOptimizationSettings.constraintConfigurations.stopToStopDistanceConstraint;
  }

  private get _largeDistanceUnitFactor(): number {
    return getMetersPerLargeDistanceUnit(this.splitService.isImperial());
  }

  private _metersToLargeDistanceUnit(meters: number | undefined): number | undefined {
    return _.isNil(meters) ? undefined : meters / this._largeDistanceUnitFactor;
  }

  private _largeDistanceUnitToMeters(value: number | undefined): number | undefined {
    return _.isNil(value) ? undefined : value * this._largeDistanceUnitFactor;
  }

  private _getStopToStopField(fieldName: string) {
    return _.get(this.form, `optimizeVisitScheduleDialogForm.${fieldName}`);
  }

  private _isStopToStopFieldCleared(fieldName: string): boolean {
    const field = this._getStopToStopField(fieldName);
    return field && field.$dirty && _.isFunction(field.$isEmpty) && field.$isEmpty(field.$viewValue);
  }

  private _clearStopToStopFieldValue(fieldName: string) {
    const field = this._getStopToStopField(fieldName);
    if (!field) {
      return;
    }
    if (_.isFunction(field.$setViewValue)) {
      field.$setViewValue(undefined);
    }
    if (_.isFunction(field.$render)) {
      field.$render();
    }
    if (_.isFunction(field.$setPristine)) {
      field.$setPristine();
    }
    if (_.isFunction(field.$setUntouched)) {
      field.$setUntouched();
    }
  }

  private _revalidateStopToStopDependents(fieldNames: string[]) {
    const section = _.get(this.form, 'optimizeVisitScheduleDialogForm');
    _.forEach(fieldNames, (name) => {
      const field = _.get(section, name);
      if (field && _.isFunction(field.$validate)) {
        field.$validate();
      }
    });
  }
}

angular
  .module('bringoz.admin.scheduling-project')
  .component('optimizeVisitScheduleDialogForm', {
    bindings: {
      form: '<?',
      isTemplate: '<?',
      visitScheduleOptimizationSettings: '<',
      visitDays: '<',
      tabs: '<',
      selectedVisitDays: '=',
      scheduleProject: '<',
      providerOptions: '<?',
      templateProviderSelection: '<?',
      visitScheduleDispatchSettings: '=',
      visitScheduleResourceSettings: '=',
      visitPlanSettings: '=',
      templateAccessControls: '=',
      onSelectServiceTimeProvider: '&',
      onSelectUnitsProvider: '&',
      onSelectDeliveryTimeProvider: '&',
      onSelectServiceTimeVisitCallPredictionProvider: '&',
    },
    template: require('./optimize-visit-schedule-dialog-form.html').default,
    controller: OptimizeVisitScheduleDialogFormComponent
  });
